// filepath: apps/web/src/renderers/form/fields/ReferenceField.tsx
//
// FR-UI-FORM-1 AC-2 — reference field. The picker (search + select)
// is delegated to ReferencePicker; this component is the thin shell
// that wires FieldComponentProps → ReferencePicker props.
//
// RENDERER DISCIPLINE:
//   - Label / help resolved from uiSchema via the FR-UI-CORE-9 helper.
//   - aria-required / aria-describedby wired from uiSchema.
//   - fieldError prop is forwarded as aria-invalid + aria-describedby
//     pairing (FR-UI-CORE-10 AC-5 invariant).
//
// GRACEFUL DEGRADATION:
//   When the form definition predates FR-UI-FORM-1 — i.e. metadata
//   has neither `referenceEntityType` nor `referenceDisplayField` —
//   the field renders as a read-only text input showing the stored
//   id. This mirrors the pre-FR-UI-FORM-1 fallback and preserves
//   backwards compatibility with definitions that have not been
//   migrated.

import React from "react";
import type { FieldComponentProps } from "../FieldRegistry";
import { getUiSchemaField } from "../FieldRegistry";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";
import { ReferencePicker } from "./reference/ReferencePicker";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export function ReferenceField({
  fieldId,
  metadata,
  value,
  onChange,
  disabled,
  uiSchema,
  fieldError,
}: FieldComponentProps): React.ReactElement {
  const { locale } = useTranslation();
  const isMobile = useIsMobile();
  const uiField = getUiSchemaField(uiSchema, fieldId);
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);
  const helpText = resolveLocalizedValue(uiField?.help, locale, "");
  const helpId = `field-help-${fieldId}`;
  const errorId = fieldError?.id;
  const describedByParts = [helpText ? helpId : null, errorId].filter(
    (part): part is string => typeof part === "string",
  );
  const describedBy =
    describedByParts.length > 0 ? describedByParts.join(" ") : undefined;

  const refId = value === undefined || value === null ? "" : String(value);

  const refEntityType = metadata.referenceEntityType;
  const refDisplayField = metadata.referenceDisplayField;

  // FR-UI-NFR-3 AC-5: word-break prevents label truncation.
  const labelStyle: React.CSSProperties = isMobile
    ? { wordBreak: "break-word", overflowWrap: "break-word", fontWeight: 500 }
    : { fontWeight: 500 };

  // Graceful degradation: when the definition predates FR-UI-FORM-1,
  // the metadata lacks referenceEntityType / referenceDisplayField.
  // Render a read-only text input showing the stored id — no picker.
  if (
    typeof refEntityType !== "string" ||
    refEntityType.length === 0 ||
    typeof refDisplayField !== "string" ||
    refDisplayField.length === 0
  ) {
    const inputStyle: React.CSSProperties = isMobile
      ? {
          width: "100%",
          minHeight: `${MIN_TOUCH_TARGET_PX}px`,
          padding: "8px 12px",
          fontSize: "16px",
          boxSizing: "border-box",
        }
      : {};
    return (
      <div data-testid={`field-${fieldId}`} className="field field-reference">
        <label htmlFor={fieldId} style={labelStyle}>
          {labelText}
          {uiField?.required ? <span aria-hidden="true"> *</span> : null}
        </label>
        <input
          id={fieldId}
          type="text"
          name={fieldId}
          value={refId}
          readOnly
          aria-required={uiField?.required ? "true" : undefined}
          aria-describedby={describedBy}
          aria-invalid={fieldError ? "true" : undefined}
          data-testid={`input-${fieldId}`}
          style={inputStyle}
        />
        {helpText ? (
          <p
            id={helpId}
            className="field-help"
            style={
              isMobile
                ? { wordBreak: "break-word", overflowWrap: "break-word" }
                : {}
            }
          >
            {helpText}
          </p>
        ) : null}
      </div>
    );
  }

  // Picker mode: metadata.referenceEntityType / referenceDisplayField
  // are both present. Delegate to ReferencePicker (handles mobile bottom sheet).
  return (
    <div data-testid={`field-${fieldId}`} className="field field-reference">
      <label htmlFor={fieldId} style={labelStyle}>
        {labelText}
        {uiField?.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <ReferencePicker
        id={fieldId}
        entityType={refEntityType}
        displayField={refDisplayField}
        value={refId}
        onChange={onChange}
        disabled={disabled === true}
      />
      {helpText ? (
        <p
          id={helpId}
          className="field-help"
          style={
            isMobile
              ? { wordBreak: "break-word", overflowWrap: "break-word" }
              : {}
          }
        >
          {helpText}
        </p>
      ) : null}
      {/* FR-UI-CORE-10 AC-5: aria-describedby pairing on the input
          inside the picker is the picker's responsibility; this
          component surfaces the help text id via describedByParts so
          the picker's combobox inherits the help-aria link. The
          fieldError pair is also wired here for the read-only fallback. */}
      <span
        data-testid={`described-by-${fieldId}`}
        hidden
        data-described-by={describedBy ?? ""}
      />
    </div>
  );
}
